import {
  LegacyPortalDashboardAgeGroup,
  LegacyPortalDashboardGender,
  LegacyPortalDashboardProductType,
  LegacyPortalDashboardService,
  LegacyPortalDashboardTicketQuestionAnswerType,
} from "@spwn-portal/grpc";
import firebase from "firebase/app";
import useSWR from "swr";

import { useConnectClient } from "../../utility/connectWeb";

import type { ConnectError } from "@bufbuild/connect";
import type {
  LegacyPortalDashboardTicketQuestionnaireQuestion,
  LegacyPortalDashboardTicketQuestionnaireTicket,
} from "@spwn-portal/grpc";

type TicketQuestionnaireAnswerCount = {
  answer: string;
  count: number;
};

type TicketQuestionnaireTicket = {
  eventId: string;
  ticketId: string;
  ticketName: string;
  productType: "STREAMING_TICKET" | "VENUE_TICKET";
};

type TicketQuestionnaireAgeGroup =
  | "UNDER_10"
  | "FROM_10_TO_19"
  | "FROM_20_TO_29"
  | "FROM_30_TO_39"
  | "FROM_40_TO_49"
  | "FROM_50_TO_59"
  | "FROM_60_TO_69"
  | "OVER_70"
  | "NO_ANSWER";

type TicketQuestionnaireGender = "MALE" | "FEMALE" | "OTHER" | "NO_ANSWER";

export type TicketQuestionAnswer = {
  questionId: string;
  text: string;
  sourceEventIds: string[];
  displayOrder: number;
} & (
  | {
      type: "SELECT";
      options: string[];
      answerCounts: TicketQuestionnaireAnswerCount[];
      ticketAnswerAggregations: {
        ticket: TicketQuestionnaireTicket;
        respondentCount: number;
        answerCounts: TicketQuestionnaireAnswerCount[];
      }[];
    }
  | {
      type: "TEXT";
      answerDetails: {
        answer: string;
        tickets: TicketQuestionnaireTicket[];
        ageGroup: TicketQuestionnaireAgeGroup;
        gender: TicketQuestionnaireGender;
        lastAnsweredAt?: bigint;
      }[];
    }
);

export type GetTicketQuestionAnswersResponse = {
  questionAnswers: TicketQuestionAnswer[];
  bigqueryLastWrittenAt?: bigint;
};

type GetTicketQuestionAnswersError = ConnectError | Error | { message: string };

const GetTicketQuestionAnswersKey = "getTicketQuestionAnswers";

const toTicketProductType = (
  productType: LegacyPortalDashboardProductType
): TicketQuestionnaireTicket["productType"] => {
  switch (productType) {
    case LegacyPortalDashboardProductType.STREAMING_TICKET:
      return "STREAMING_TICKET";
    case LegacyPortalDashboardProductType.VENUE_TICKET:
      return "VENUE_TICKET";
    default:
      throw new Error(
        `Unknown questionnaire ticket product type: ${productType}`
      );
  }
};

const toTicket = ({
  eventId,
  ticketId,
  ticketName,
  productType,
}: LegacyPortalDashboardTicketQuestionnaireTicket): TicketQuestionnaireTicket => ({
  eventId,
  ticketId,
  ticketName,
  productType: toTicketProductType(productType),
});

const toAnswerCount = ({
  answer,
  count,
}: TicketQuestionnaireAnswerCount): TicketQuestionnaireAnswerCount => ({
  answer,
  count,
});

const toAgeGroup = (
  ageGroup: LegacyPortalDashboardAgeGroup
): TicketQuestionnaireAgeGroup => {
  switch (ageGroup) {
    case LegacyPortalDashboardAgeGroup.UNDER_10:
      return "UNDER_10";
    case LegacyPortalDashboardAgeGroup.FROM_10_TO_19:
      return "FROM_10_TO_19";
    case LegacyPortalDashboardAgeGroup.FROM_20_TO_29:
      return "FROM_20_TO_29";
    case LegacyPortalDashboardAgeGroup.FROM_30_TO_39:
      return "FROM_30_TO_39";
    case LegacyPortalDashboardAgeGroup.FROM_40_TO_49:
      return "FROM_40_TO_49";
    case LegacyPortalDashboardAgeGroup.FROM_50_TO_59:
      return "FROM_50_TO_59";
    case LegacyPortalDashboardAgeGroup.FROM_60_TO_69:
      return "FROM_60_TO_69";
    case LegacyPortalDashboardAgeGroup.OVER_70:
      return "OVER_70";
    case LegacyPortalDashboardAgeGroup.NO_ANSWER:
      return "NO_ANSWER";
    default:
      throw new Error(`Unknown questionnaire age group: ${ageGroup}`);
  }
};

const toGender = (
  gender: LegacyPortalDashboardGender
): TicketQuestionnaireGender => {
  switch (gender) {
    case LegacyPortalDashboardGender.MALE:
      return "MALE";
    case LegacyPortalDashboardGender.FEMALE:
      return "FEMALE";
    case LegacyPortalDashboardGender.OTHER:
      return "OTHER";
    case LegacyPortalDashboardGender.NO_ANSWER:
      return "NO_ANSWER";
    default:
      throw new Error(`Unknown questionnaire gender: ${gender}`);
  }
};

export const toTicketQuestionAnswer = (
  question: LegacyPortalDashboardTicketQuestionnaireQuestion
): TicketQuestionAnswer => {
  const commonValues = {
    questionId: question.questionId,
    text: question.text,
    sourceEventIds: [...question.sourceEventIds],
    displayOrder: question.displayOrder,
  };

  if (
    question.type === LegacyPortalDashboardTicketQuestionAnswerType.SELECT &&
    question.answerValues.case === "selectAnswerAggregation"
  ) {
    const { options, answerCounts, ticketAnswerAggregations } =
      question.answerValues.value;

    return {
      ...commonValues,
      type: "SELECT",
      options: [...options],
      answerCounts: answerCounts.map(toAnswerCount),
      ticketAnswerAggregations: ticketAnswerAggregations.map(
        ({ ticket, respondentCount, answerCounts }) => {
          if (ticket === undefined) {
            throw new Error(
              `Missing ticket in questionnaire aggregation: ${question.questionId}`
            );
          }

          return {
            ticket: toTicket(ticket),
            respondentCount,
            answerCounts: answerCounts.map(toAnswerCount),
          };
        }
      ),
    };
  }

  if (
    question.type === LegacyPortalDashboardTicketQuestionAnswerType.TEXT &&
    question.answerValues.case === "textAnswers"
  ) {
    return {
      ...commonValues,
      type: "TEXT",
      answerDetails: question.answerValues.value.answerDetails.map(
        ({ answer, tickets, ageGroup, gender, lastAnsweredAt }) => ({
          answer,
          tickets: tickets.map(toTicket),
          ageGroup: toAgeGroup(ageGroup),
          gender: toGender(gender),
          lastAnsweredAt,
        })
      ),
    };
  }

  throw new Error(
    `Unknown questionnaire question type: ${question.type}, case: ${
      question.answerValues.case ?? ""
    }`
  );
};

export const useGetTicketQuestionAnswers = (
  eventIds: string[],
  fromDate?: bigint,
  toDate?: bigint
) => {
  const currentUserId = firebase.auth().currentUser?.uid ?? null;
  const connectClient = useConnectClient(LegacyPortalDashboardService);
  const uniqueEventIds = Array.from(new Set(eventIds)).sort();
  const uniqueEventIdsKey = JSON.stringify(uniqueEventIds);

  const {
    data: fetchedData,
    isLoading,
    isValidating,
    error,
    mutate,
  } = useSWR<GetTicketQuestionAnswersResponse, GetTicketQuestionAnswersError>(
    currentUserId && uniqueEventIds.length > 0
      ? [
          GetTicketQuestionAnswersKey,
          currentUserId,
          uniqueEventIdsKey,
          fromDate?.toString() ?? "",
          toDate?.toString() ?? "",
        ]
      : null,
    async () => {
      try {
        const response = await connectClient.getTicketQuestionnaireResults({
          eventIds: uniqueEventIds,
          fromDate,
          toDate,
        });

        return {
          questionAnswers: response.questions.map(toTicketQuestionAnswer),
          bigqueryLastWrittenAt: response.bigqueryLastWrittenAt,
        };
      } catch (error) {
        console.error("Error fetching ticket questionnaire results:", error);
        throw error;
      }
    }
  );

  return {
    eventIds: uniqueEventIds,
    isLoading,
    isValidating,
    error,
    mutate,
    fetchedData,
  };
};
